export const CV_PROFILE_LINK_PLATFORMS = [
  "linkedin",
  "github",
  "dribbble",
  "behance",
] as const;

export type CvProfileLinkPlatform = (typeof CV_PROFILE_LINK_PLATFORMS)[number];
export type CvLinkKey = "website" | CvProfileLinkPlatform;

type PlatformLinkSpec = {
  displayPrefix: string;
  canonicalPrefix: string;
  host: string;
  pathPrefix: string;
  /**
   * The handle is the first path segment and anything after it is a view of
   * the same profile (LinkedIn's `/in/jane/details/experience/`, or a
   * secondary-language profile at `/in/jane/ar`).
   */
  ignoreSubpath?: boolean;
};

const PLATFORM_LINK_SPECS: Record<CvProfileLinkPlatform, PlatformLinkSpec> = {
  linkedin: {
    displayPrefix: "linkedin.com/in/",
    canonicalPrefix: "https://linkedin.com/in/",
    host: "linkedin.com",
    pathPrefix: "/in/",
    ignoreSubpath: true,
  },
  github: {
    displayPrefix: "github.com/",
    canonicalPrefix: "https://github.com/",
    host: "github.com",
    pathPrefix: "/",
  },
  dribbble: {
    displayPrefix: "dribbble.com/",
    canonicalPrefix: "https://dribbble.com/",
    host: "dribbble.com",
    pathPrefix: "/",
  },
  behance: {
    displayPrefix: "behance.net/",
    canonicalPrefix: "https://behance.net/",
    host: "behance.net",
    pathPrefix: "/",
  },
};

const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
const HOST_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
// A handle may carry percent-encoded bytes: LinkedIn custom URLs can be
// non-Latin (an Arabic slug arrives as `/in/%D9%85...`).
const PROFILE_HANDLE_PATTERN =
  /^(?:[a-zA-Z0-9]|%[0-9A-Fa-f]{2})(?:[a-zA-Z0-9._~-]|%[0-9A-Fa-f]{2})*$/;
const NON_ASCII_PATTERN = /[^\x00-\x7F]/;

/** Accept "@jane" and a typed non-Latin handle; percent-encode the latter. */
function toHandleCandidate(value: string): string {
  const bare = value.replace(/^@+/, "");
  return NON_ASCII_PATTERN.test(bare) ? encodeURIComponent(bare) : bare;
}

function decodeHandle(handle: string): string {
  try {
    return decodeURIComponent(handle);
  } catch {
    return handle;
  }
}

function hostMatches(hostname: string, expected: string) {
  return hostname === expected || hostname.endsWith(`.${expected}`);
}

function trimRootSlash(url: URL) {
  if (url.pathname === "/" && !url.search && !url.hash) {
    return url.toString().replace(/\/$/, "");
  }
  return url.toString();
}

function parseWebsite(value: string): URL | null {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;

  const candidate = SCHEME_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/{2}/, "")}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return null;
  }
  if (parsed.username || parsed.password || parsed.port) return null;
  if (!HOST_PATTERN.test(parsed.hostname)) return null;
  return parsed;
}

function getPlatformHandle(
  platform: CvProfileLinkPlatform,
  value: string,
): string | null {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;

  const spec = PLATFORM_LINK_SPECS[platform];
  const looksLikeUrl = SCHEME_PATTERN.test(trimmed) || trimmed.includes("/");

  if (!looksLikeUrl) {
    const handle = toHandleCandidate(trimmed);
    return PROFILE_HANDLE_PATTERN.test(handle) ? handle : null;
  }

  const candidate = SCHEME_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/{2}/, "")}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return null;
  }
  if (parsed.username || parsed.password || parsed.port) return null;
  if (!hostMatches(parsed.hostname.toLowerCase(), spec.host)) return null;
  if (!parsed.pathname.toLowerCase().startsWith(spec.pathPrefix)) return null;

  const rest = parsed.pathname
    .slice(spec.pathPrefix.length)
    .replace(/\/+$/, "");
  const handle = spec.ignoreSubpath ? rest.split("/")[0] : rest;
  if (!handle || handle.includes("/")) return null;
  return PROFILE_HANDLE_PATTERN.test(handle) ? handle : null;
}

export function isCvLinkKey(value: string): value is CvLinkKey {
  return value === "website" || isCvProfileLinkPlatform(value);
}

export function isCvProfileLinkPlatform(
  value: string,
): value is CvProfileLinkPlatform {
  return (CV_PROFILE_LINK_PLATFORMS as readonly string[]).includes(value);
}

/** The fixed, non-editable prefix shown beside the CV link input. */
export function getCvLinkInputPrefix(key: CvLinkKey): string {
  return key === "website"
    ? "https://"
    : PLATFORM_LINK_SPECS[key].displayPrefix;
}

/**
 * Convert a stored URL back into the editable domain/handle portion. Invalid
 * legacy values are returned unchanged so the editor exposes the error rather
 * than silently deleting user data.
 */
export function getCvLinkInputValue(
  key: CvLinkKey,
  stored?: string | null,
): string | undefined {
  if (stored === undefined || stored === null) return undefined;
  const trimmed = stored.trim();
  if (!trimmed) return "";

  if (key === "website") {
    const parsed = parseWebsite(trimmed);
    if (!parsed) return trimmed;
    return `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`.replace(
      /\/$/,
      "",
    );
  }

  const handle = getPlatformHandle(key, trimmed);
  return handle ? decodeHandle(handle) : trimmed;
}

/**
 * Collapse a pasted full link to the part shown after the fixed prefix, so
 * "https://www.linkedin.com/in/jane-doe/?utm_source=share" becomes "jane-doe"
 * and "https://mysite.com/" becomes "mysite.com". Anything that is not a
 * recognised full link (a bare handle, a half-typed value, a wrong-domain
 * URL) is returned unchanged so validation can still flag it.
 */
export function collapseCvLinkInput(key: CvLinkKey, input: string): string {
  const trimmed = input.trim();
  const isFullLink =
    key === "website"
      ? SCHEME_PATTERN.test(trimmed)
      : SCHEME_PATTERN.test(trimmed) || trimmed.includes("/");
  if (!isFullLink) return input;
  const normalized = normalizeCvLinkValue(key, trimmed);
  return normalized ? (getCvLinkInputValue(key, normalized) ?? input) : input;
}

/**
 * Validate the editable portion. Social fields accept either a profile handle
 * ("jane-doe", "@jane-doe") or that platform's own full link
 * ("https://www.linkedin.com/in/jane-doe/"): many people do not know their
 * handle and paste the URL. A link on another domain is still rejected, so
 * the field cannot be pointed at an unrelated website.
 */
export function validateCvLinkInput(
  key: CvLinkKey,
  input: string,
): "url" | "domain" | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (key === "website") {
    return parseWebsite(trimmed) ? null : "domain";
  }

  return getPlatformHandle(key, trimmed) ? null : "url";
}

/** Convert an editable domain/handle into the canonical persisted URL. */
export function composeCvLink(key: CvLinkKey, input?: string | null): string {
  return normalizeCvLinkValue(key, input) ?? "";
}

/**
 * Normalize persisted, imported, or API-supplied CV links. Platform fields
 * accept either a bare handle or their own official URL and always return an
 * HTTPS URL on that platform. Wrong-domain values fail closed.
 */
export function normalizeCvLinkValue(
  key: CvLinkKey,
  value?: string | null,
): string | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;

  if (key === "website") {
    const parsed = parseWebsite(trimmed);
    return parsed ? trimRootSlash(parsed) : null;
  }

  const handle = getPlatformHandle(key, trimmed);
  return handle ? `${PLATFORM_LINK_SPECS[key].canonicalPrefix}${handle}` : null;
}

export function resolveCvLink(
  key: CvLinkKey,
  value?: string | null,
): { label: string; href: string } | null {
  const href = normalizeCvLinkValue(key, value);
  if (!href) return null;
  return {
    href,
    label: decodeHandle(href.replace(/^https?:\/\//i, "").replace(/\/$/, "")),
  };
}

export function normalizeCvLinkPatch(
  input: Partial<Record<CvLinkKey, string | undefined>>,
): {
  links: Partial<Record<CvLinkKey, string>>;
  invalidKey: CvLinkKey | null;
} {
  const links: Partial<Record<CvLinkKey, string>> = {};
  const keys: CvLinkKey[] = ["website", ...CV_PROFILE_LINK_PLATFORMS];

  for (const key of keys) {
    const value = input[key];
    if (value === undefined) continue;
    if (!value.trim()) {
      links[key] = "";
      continue;
    }
    const normalized = normalizeCvLinkValue(key, value);
    if (!normalized) return { links: {}, invalidKey: key };
    links[key] = normalized;
  }

  return { links, invalidKey: null };
}
